import { randomUUID } from "@-/com.npmjs.expo-crypto";
import ensureError from "@-/com.npmjs.ensure-error";
import { hasActorBackupSnapshotReader } from "@-/dev.improve.haniverse.lib.database.engine";
import {
  backupYield,
  readBackupExport,
  readBackupManifest,
  writeBackupValue,
} from "#backup-archive";
import { backupStore } from "#backup-store";
import { backupTables } from "#backup-tables";
import { BackupFailure, toBackupFailure } from "#backup-failure";
import type { BackupEngine, BackupListing } from "#backup-engine-types";
import {
  archiveBackupManifestSchema,
  isBackupExpired,
  type ArchiveBackupManifest,
} from "#backup-types";

export const usesNativeBackupEngine = false;

/** Rows per encrypted chunk in the browser's backup store. */
const QUOTA_MESSAGE = /quota/iu;

const CHUNK_ROWS = 64;

function isQuotaError(error: unknown): boolean {
  const name: unknown = typeof error === "object" && error !== null ? Reflect.get(error, "name") : null;
  return name === "QuotaExceededError" || QUOTA_MESSAGE.test(ensureError(error).message);
}

function storeFailure(error: unknown, stage: string): BackupFailure {
  if (isQuotaError(error)) {
    return new BackupFailure(
      {
        detail: `The browser refused to store more data for this site (${ensureError(error).message})`,
        kind: "storage-full",
        stage,
      },
      { cause: error },
    );
  }
  return toBackupFailure(error, stage);
}

async function removeBackup(id: string): Promise<void> {
  // Remove the publication record first: an interrupted deletion cannot expose a recoverable
  // manifest pointing at partially removed chunks.
  await backupStore.remove(`manifest/${id}`);
  for (const key of await backupStore.keys(`archive/${id}/`)) {
    await backupStore.remove(key);
  }
  await backupStore.remove(`pending/${id}`);
  await backupStore.remove(`created/${id}`);
}

async function listManifests(): Promise<BackupListing> {
  const backups: ArchiveBackupManifest[] = [];
  const problems: string[] = [];
  for (const key of await backupStore.keys("manifest/")) {
    const id = key.slice("manifest/".length);
    try {
      backups.push(await readBackupManifest(id));
    } catch (error) {
      problems.push(`Backup ${id} could not be verified: ${ensureError(error).message}`);
    }
  }
  return { backups, problems };
}

export const backupEngine: BackupEngine = {
  async create(request, operation) {
    const connection = request.db?.$client;
    if (request.db === null || !hasActorBackupSnapshotReader(connection)) {
      return null;
    }
    // One hold of the leader's connection: every table from one committed state. No later page
    // can find the database changed, so activity never fails a backup.
    operation.report({ completed: 0, label: "Reading study data", total: 2 });
    let rows: Record<string, unknown[]>;
    try {
      rows = await operation.wait(
        connection.readBackupSnapshot(backupTables.map((table) => table.name)),
      );
    } catch (error) {
      throw toBackupFailure(error, "capture");
    }
    const id = randomUUID();
    const chunks: ArchiveBackupManifest["chunks"] = [];
    let rowCount = 0;
    try {
      await backupStore.set(`pending/${id}`, String(request.createdAt));
      for (const [index, table] of backupTables.entries()) {
        const parsed = table.parse(table.decode(rows[table.name] ?? []));
        for (let start = 0; start < parsed.length; start += CHUNK_ROWS) {
          const page = parsed.slice(start, start + CHUNK_ROWS);
          const key = `archive/${id}/${chunks.length}`;
          await writeBackupValue(key, page, operation);
          chunks.push({ key, rows: page.length, table: table.key });
          rowCount += page.length;
          operation.report({
            completed: index,
            label: `Saving backup · ${rowCount.toLocaleString()} records`,
            total: backupTables.length,
          });
          await backupYield();
        }
      }
      const manifest = archiveBackupManifestSchema.parse({
        chunks,
        createdAt: request.createdAt,
        id,
        learningPreferences: request.learningPreferences,
        reason: request.reason,
        rowCount,
        sourceUserId: request.sourceUserId,
        version: 1,
      });
      operation.check();
      await backupStore.set(`created/${id}`, String(request.createdAt));
      await writeBackupValue(`manifest/${id}`, manifest, operation);
      await backupStore.remove(`pending/${id}`);
      return manifest;
    } catch (error) {
      await removeBackup(id).catch(() => {});
      throw storeFailure(error, "publish");
    }
  },

  async list() {
    try {
      return await listManifests();
    } catch (error) {
      throw storeFailure(error, "list");
    }
  },

  async prune(now, keep) {
    const problems: string[] = [];
    const lastClock = Number(await backupStore.get("last-clock"));
    // A clock jump must not immediately erase recovery history. Retention still filters the list;
    // keep physical archives until a subsequent observation.
    await backupStore.set("last-clock", String(now));
    if (lastClock > 0 && (now < lastClock || now - lastClock > 7 * 24 * 60 * 60 * 1000)) {
      return problems;
    }
    const remove = async (id: string) => {
      try {
        await removeBackup(id);
      } catch (error) {
        problems.push(`Could not remove ${id}: ${ensureError(error).message}`);
      }
    };
    const listing = await listManifests();
    for (const manifest of listing.backups) {
      if (isBackupExpired(manifest.createdAt, now) && !keep.has(manifest.id)) {
        await remove(manifest.id);
      }
    }
    // The creation index is independent of the encrypted manifest, so a corrupt publication
    // cannot leak storage forever.
    for (const key of await backupStore.keys("created/")) {
      const createdAt = Number(await backupStore.get(key));
      const id = key.slice("created/".length);
      if (createdAt > 0 && isBackupExpired(createdAt, now) && !keep.has(id)) {
        await remove(id);
      }
    }
    // Under the cross-tab job lock, no live writer owns a pending-only job.
    for (const key of await backupStore.keys("pending/")) {
      const id = key.slice("pending/".length);
      if ((await backupStore.get(`manifest/${id}`)) === null) {
        await remove(id);
      } else {
        await backupStore.remove(key);
      }
    }
    return problems;
  },

  async readExport(manifest, operation) {
    if (manifest.version !== 1) {
      throw new BackupFailure({
        detail: "This backup was saved by the iOS or Android app",
        kind: "unreadable",
        stage: "read",
      });
    }
    try {
      return await readBackupExport(manifest, undefined, operation);
    } catch (error) {
      throw new BackupFailure(
        { detail: ensureError(error).message, kind: "unreadable", stage: "read" },
        { cause: error },
      );
    }
  },

  readLegacyRecoveryHold: () => backupStore.get("recovery-hold"),

  async writeRecoveryHold(held) {
    await (held ? backupStore.set("recovery-hold", "true") : backupStore.remove("recovery-hold"));
  },
};
